import { type FC } from 'react';

import { alpha } from '@mui/material/styles';

import { mergeSx } from '../../util/mui/sx';
import { Box, type BoxProps } from '../structural/Box';

export interface ProgressSegment {
  /** MUI sx color path resolved from the theme palette, e.g. `'success.main'`, `'error.main'` */
  color: string;
  /** Percentage value 0–100 */
  value: number;
}

export interface MultiToneProgressBarProps extends Omit<BoxProps, 'children' | 'role'> {
  readonly 'aria-label': string;
  readonly segments: ProgressSegment[];
}

/**
 * A generic segmented progress bar. Each entry in `segments` is rendered as a colored band in
 * the order provided. Segments with a value of `0` are omitted. Any remaining width is filled
 * with the theme's muted primary background color, matching the appearance of MUI's LinearProgress
 * buffer variant.
 */
export const MultiToneProgressBar: FC<MultiToneProgressBarProps> = ({
  'aria-label': ariaLabel,
  segments,
  sx,
  ...props
}) => {
  const progressValue = Math.min(
    100,
    Math.max(
      0,
      segments.reduce((total, segment) => total + segment.value, 0),
    ),
  );

  return (
    <Box
      {...props}
      aria-label={ariaLabel}
      aria-valuemax={100}
      aria-valuemin={0}
      aria-valuenow={progressValue}
      role='progressbar'
      sx={mergeSx(
        t => ({
          backgroundColor: alpha(t.palette.primary.main, 0.2),
          borderRadius: 1,
          display: 'flex',
          height: 4,
          overflow: 'hidden',
        }),
        sx,
      )}
    >
      {segments
        .filter(s => s.value > 0)
        .map((segment, index) => (
          <Box
            key={index}
            sx={{
              backgroundColor: segment.color,
              borderRight: t => `2px solid ${t.palette.common.white}`,
              height: '100%',
              opacity: 0.8,
              width: `${segment.value}%`,
            }}
          />
        ))}
    </Box>
  );
};
